import React, { useEffect, useMemo, useState } from "react";
import { api, fmtSigned } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Brain, RefreshCw, Sparkles, TrendingUp, TrendingDown, Minus, Database, Target } from "lucide-react";
import { LineChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { toast } from "sonner";

const fmtNum = (v, d = 2) => (v == null ? "—" : Number(v).toFixed(d));

export default function Factors() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [training, setTraining] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/factors/weights").then((r) => setData(r.data)).finally(() => setLoading(false));
  };

  useEffect(load, []);

  const runTraining = async () => {
    setTraining(true);
    try {
      const r = await api.post("/factors/train");
      const s = r.data?.samples ?? 0;
      if (s > 0) {
        toast.success(`Trained on ${s} new sample${s === 1 ? "" : "s"} · MSE ${fmtNum(r.data.mse, 1)}`);
      } else {
        toast.info("No new settled games to learn from yet. Train once results come in.");
      }
      load();
    } catch (e) {
      toast.error("Training failed");
    } finally {
      setTraining(false);
    }
  };

  const weightsAbsSorted = useMemo(() => {
    if (!data) return [];
    return [...data.factors].sort((a, b) => Math.abs(b.weight) - Math.abs(a.weight));
  }, [data]);

  const history = useMemo(() => {
    if (!data?.history) return [];
    return data.history.map((h, i) => ({
      i,
      loss: h.loss_ema,
      samples: h.total_training_count,
      at: h.at,
    }));
  }, [data]);

  return (
    <div className="mx-auto max-w-7xl space-y-6" data-testid="factors-page">
      <PageHeader
        title="Self-learning engine"
        subtitle="14-factor model that adapts its weights from real outcomes. Every settled bet sharpens the next prediction."
        actions={
          <button
            onClick={runTraining}
            disabled={training}
            className="iso-btn-primary"
            data-testid="factors-train-btn"
          >
            {training
              ? <><RefreshCw className="size-3.5 animate-spin" /> Training...</>
              : <><Sparkles className="size-3.5" /> Run training</>}
          </button>
        }
      />

      {loading || !data ? (
        <div className="iso-card p-12 text-center text-sm text-gray-500">Loading model state…</div>
      ) : (
        <>
          {/* KPI tiles */}
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <Tile icon={Database} label="Samples trained" value={data.training_count} accent="primary" testid="stat-training-count" />
            <Tile icon={Target} label="Loss (EMA)" value={data.loss_ema == null ? "—" : fmtNum(data.loss_ema, 2)}
              accent="secondary" testid="stat-loss-ema" />
            <Tile icon={Brain} label="Pending outcome" value={data.dataset.pending_outcome} accent="orange"
              hint="Simulations waiting for final scores" testid="stat-pending" />
            <Tile icon={Sparkles} label="Ready to train" value={data.dataset.awaiting_training} accent="green"
              hint="Settled snapshots queued for the next pass" testid="stat-queued" />
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            {/* Weight table */}
            <div className="iso-card lg:col-span-2">
              <div className="flex items-center justify-between border-b border-border px-6 py-4">
                <div>
                  <h3 className="font-lexend">Factor weights</h3>
                  <p className="text-xs text-gray-500">Sorted by magnitude. Positive values push predicted margin toward the home team.</p>
                </div>
              </div>
              <div className="divide-y divide-border">
                {weightsAbsSorted.map((f) => (
                  <WeightRow key={f.name} f={f} />
                ))}
              </div>
            </div>

            {/* Loss history chart */}
            <div className="iso-card">
              <div className="border-b border-border px-6 py-4">
                <h3 className="font-lexend">Loss over time</h3>
                <p className="text-xs text-gray-500">MSE exponential moving average across training passes.</p>
              </div>
              <div className="p-4">
                {history.length === 0 ? (
                  <div className="flex h-56 items-center justify-center text-center text-xs text-gray-500">
                    No training history yet. Settle a few bets and the model starts learning.
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={220}>
                    <LineChart data={history}>
                      <XAxis dataKey="i" hide />
                      <YAxis tick={{ fontSize: 10, fill: "#9ca3af" }} width={36} />
                      <Tooltip
                        contentStyle={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 11 }}
                        formatter={(v) => fmtNum(v, 2)}
                        labelFormatter={(i) => `Pass ${Number(i) + 1}`}
                      />
                      <Line type="monotone" dataKey="loss" stroke="#4e36f5" strokeWidth={2} dot={{ r: 2.5 }} />
                    </LineChart>
                  </ResponsiveContainer>
                )}
                <div className="mt-4 space-y-1.5 text-xs text-gray-500">
                  <div className="flex items-center justify-between">
                    <span>Total snapshots</span>
                    <span className="font-semibold text-gray-900">{data.dataset.total}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Settled</span>
                    <span className="font-semibold text-gray-900">{data.dataset.settled}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Used for training</span>
                    <span className="font-semibold text-gray-900">{data.dataset.trained}</span>
                  </div>
                  {data.last_trained_at && (
                    <div className="flex items-center justify-between pt-2 border-t border-border text-[11px]">
                      <span>Last trained</span>
                      <span className="text-gray-900">{new Date(data.last_trained_at).toLocaleString()}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Explainer strip */}
          <div className="iso-card p-5">
            <div className="flex items-start gap-3">
              <div className="flex size-10 items-center justify-center rounded-xl bg-secondary/10 text-secondary">
                <Brain className="size-5" />
              </div>
              <div>
                <h4 className="font-lexend">How the learning loop works</h4>
                <p className="mt-1 text-xs leading-relaxed text-gray-600">
                  Every Monte Carlo simulation records a factor snapshot. When the game finishes and its score is pulled
                  via The Odds API, we compare the model's predicted margin to the actual. A small gradient step pushes
                  each factor weight toward reducing that error — L2-regularized, capped at ±5.0, and clipped to ±{Math.round(0.75 * 100)}%
                  of the league margin sigma so no single pass can wreck the simulator. Replay this page after a few
                  settlements and you'll see the weights drift into sensible territory.
                </p>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

const Tile = ({ icon: Icon, label, value, accent = "primary", hint, testid }) => {
  const bg = {
    primary: "bg-primary text-primary-foreground",
    secondary: "bg-secondary text-white",
    green: "bg-green text-white",
    orange: "bg-orange text-white",
  }[accent];
  return (
    <div className="iso-card p-5" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="iso-label !mb-0">{label}</div>
        <div className={`flex size-8 items-center justify-center rounded-lg ${bg}`}>
          <Icon className="size-4" />
        </div>
      </div>
      <div className="mt-2 font-lexend text-2xl font-bold text-gray-900">{value}</div>
      {hint && <div className="mt-1 text-[11px] text-gray-500">{hint}</div>}
    </div>
  );
};

const WeightRow = ({ f }) => {
  const mag = Math.abs(f.weight);
  // Scale bar width to max expected |weight| ~ 1.0 for visual sanity
  const barWidth = Math.min(100, (mag / 1.0) * 100);
  const sign = f.weight > 1e-4 ? "pos" : f.weight < -1e-4 ? "neg" : "neu";
  const SignIcon = sign === "pos" ? TrendingUp : sign === "neg" ? TrendingDown : Minus;
  const signColor = sign === "pos" ? "text-green-dark" : sign === "neg" ? "text-red-dark" : "text-gray-400";
  return (
    <div className="flex items-center gap-4 px-6 py-3" data-testid={`factor-row-${f.name}`}>
      <div className="flex size-8 items-center justify-center rounded-lg bg-gray-50 text-gray-500">
        <SignIcon className={`size-4 ${signColor}`} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-gray-900">{f.label}</div>
        <div className="text-[11px] uppercase tracking-wider text-gray-400">{f.name}</div>
      </div>
      <div className="flex items-center gap-3">
        <div className="w-32 hidden md:block">
          <div className="h-1.5 rounded-full bg-gray-100">
            <div
              className={`h-full rounded-full ${sign === "pos" ? "bg-green" : sign === "neg" ? "bg-red" : "bg-gray-300"}`}
              style={{ width: `${barWidth}%` }}
            />
          </div>
        </div>
        <div className={`font-lexend text-sm font-bold tabular-nums ${signColor}`}>
          {fmtSigned(f.weight, 3)}
        </div>
      </div>
    </div>
  );
};
